import { useMemo, useState } from "react";
import { ArrowRight, CalendarCheck, Plus, Trash2 } from "lucide-react";
import { useEvents, useHealthDemandMutations, useHealthDemands, useHealthRoutineMutations, useHealthRoutines, type RoutineInput } from "../api/hooks";
import type { CalEvent, HealthDemand, HealthRoutine } from "../api/types";
import { MONTHS_SHORT, WEEKDAYS_SHORT, addDays, addMonths, datePart, diffDays, formatTime, minutesOf, parts, weekday, type DateStr } from "../lib/date";
import { newDraft } from "../lib/draft";
import { anchorOf, useUi } from "../state/ui";
import { CollapsibleSection, DoneSection, PageHead, RowSection } from "./TasksPage";
import { Segmented, StatusChip, useStoredTab } from "./life";
import "./pages.css";
import "./life.css";

type Tab = "consultas" | "demandas" | "cuidados";

/** "12/04/2026" */
export function brDate(d: DateStr): string {
  const { y, m, d: day } = parts(d);
  return `${String(day).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

/** "qua., 15 de out." (+ year when not this year). */
function dayLabel(d: DateStr, today: DateStr): string {
  const { y, m, d: day } = parts(d);
  return `${WEEKDAYS_SHORT[weekday(d)]}, ${day} de ${MONTHS_SHORT[m - 1]}${y !== parts(today).y ? ` de ${y}` : ""}`;
}

// --- Consultas: agenda events with category "saude" ------------------------------------------

/** Window of the agenda the Consultas tab looks at. */
const PAST_DAYS = 60;
const AHEAD_DAYS = 330;

/** Health events from the agenda: one row per series (its next occurrence), upcoming and recent. */
function useAppointments(today: DateStr) {
  const { data, isLoading } = useEvents(addDays(today, -PAST_DAYS), addDays(today, AHEAD_DAYS));
  return useMemo(() => {
    // A pet's vet visits and vaccines are consultas too.
    const health = (data?.events ?? []).filter((e) => e.category === "saude" || e.category === "pets");
    const upcoming: CalEvent[] = [];
    const past: CalEvent[] = [];
    const seen = new Set<string>();
    for (const e of [...health].sort((a, b) => a.start.localeCompare(b.start))) {
      // All-day ends are exclusive.
      if (e.allDay ? datePart(e.end) <= today : datePart(e.start) < today) {
        past.push(e);
        continue;
      }
      const series = e.seriesId ?? e.eventId;
      if (e.isRecurring && seen.has(series)) continue;
      seen.add(series);
      upcoming.push(e);
    }
    return { upcoming, past: past.reverse(), isLoading };
  }, [data, isLoading, today]);
}

function AppointmentRow({ e }: { e: CalEvent }) {
  const ui = useUi();
  const day = datePart(e.start);
  const { m, d } = parts(day);
  const when = [e.allDay ? "Dia inteiro" : formatTime(minutesOf(e.start)), e.location].filter(Boolean).join(" · ");
  const sub = e.categoryLabel?.split("›").pop()?.trim();
  return (
    <li className="row appt-row">
      <span className="date-tile" aria-hidden>
        <span className="date-tile-m">{MONTHS_SHORT[m - 1].replace(".", "")}</span>
        <span className="date-tile-d">{d}</span>
      </span>
      <button className="row-main" onClick={(ev) => ui.setOverlay({ kind: "details", event: e, anchor: anchorOf(ev.currentTarget) })}>
        <span className="row-stack">
          <span className="row-title">{e.title || "(sem título)"}</span>
          <span className="row-sub">
            {dayLabel(day, ui.today)}
            {when && ` · ${when}`}
            {e.recurrenceText && ` · ${e.recurrenceText}`}
          </span>
        </span>
      </button>
      <span className="row-meta">
        {day === ui.today && <span className="chip accent">Hoje</span>}
        {sub && sub !== "Saúde" && <span className="row-when">{sub}</span>}
      </span>
    </li>
  );
}

function Appointments() {
  const ui = useUi();
  const { upcoming, past, isLoading } = useAppointments(ui.today);
  const book = () =>
    ui.setOverlay({
      kind: "editor",
      draft: { ...newDraft({ date: addDays(ui.today, 1), startMin: 9 * 60, settings: ui.me.settings, calendars: ui.calendars }), category: "saude" }
    });
  return (
    <>
      <div className="life-actions">
        <button className="btn primary" onClick={book}>
          <Plus size={16} strokeWidth={2.4} /> Marcar consulta
        </button>
        <span className="life-hint">Eventos da agenda com a categoria Saúde ou Pets — inclusive os marcados pelo WhatsApp.</span>
      </div>
      <ToSchedule />
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {!upcoming.length ? (
            <p className="empty">Nenhuma consulta marcada.</p>
          ) : (
            <RowSection title="Próximas" count={upcoming.length}>
              {upcoming.map((e) => <AppointmentRow key={e.key} e={e} />)}
            </RowSection>
          )}
          <DoneSection title={`Últimos ${PAST_DAYS} dias`} count={past.length}>
            {past.map((e) => <AppointmentRow key={e.key} e={e} />)}
          </DoneSection>
        </>
      )}
    </>
  );
}

/** "A agendar": consultas still to be booked, checked off once they're on the agenda. */
function ToSchedule() {
  const { data } = useHealthDemands();
  const pending = (data?.pending ?? []).filter((d) => d.kind === "schedule");
  return (
    <section className="row-section">
      <h2 className="row-section-title eyebrow">
        A agendar <span>{String(pending.length).padStart(2, "0")}</span>
      </h2>
      <DemandAdd kind="schedule" />
      {pending.length > 0 && (
        <ul className="rows">
          {pending.map((d) => <DemandRow key={d.id} item={d} />)}
        </ul>
      )}
    </section>
  );
}

// --- Demandas do médico ------------------------------------------------------------------------

/** "qua., 30 de out. · 14:00 · na agenda". */
function DueLine({ item, today }: { item: HealthDemand; today: DateStr }) {
  if (!item.dueDate) return null;
  const late = !item.completed && item.dueDate < today;
  return (
    <span className={`due-line${late ? " late" : ""}`}>
      {item.onAgenda && <CalendarCheck size={13} aria-label="Na agenda, com lembrete" />}
      {item.dueDate === today ? "Hoje" : dayLabel(item.dueDate, today)}
      {item.dueTime && ` · ${item.dueTime}`}
      {late && " · atrasada"}
    </span>
  );
}

function DemandRow({ item }: { item: HealthDemand }) {
  const ui = useUi();
  const { create, update, remove } = useHealthDemandMutations();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [origin, setOrigin] = useState(item.origin ?? "");
  const [dueDate, setDueDate] = useState(item.dueDate ?? "");
  const [dueTime, setDueTime] = useState(item.dueTime ?? "");
  const saving = item.id.startsWith("tmp-");
  const fail = (err: unknown) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`);

  const toggle = () =>
    update.mutate(
      { item, completed: !item.completed },
      {
        onSuccess: () =>
          !item.completed && ui.showToast(item.kind === "schedule" ? "Consulta agendada" : "Demanda resolvida", { label: "Desfazer", run: () => update.mutate({ item: { ...item, completed: true }, completed: false }) }),
        onError: fail
      }
    );

  const save = () => {
    setEditing(false);
    const t = title.trim();
    const o = origin.trim();
    if (!t) {
      setTitle(item.title);
      return;
    }
    const day = dueDate || null;
    const time = (day && dueTime) || null;
    if (t !== item.title || o !== (item.origin ?? "") || day !== item.dueDate || time !== item.dueTime) {
      update.mutate({ item, title: t, origin: o || null, dueDate: day, dueTime: time }, { onError: fail });
    }
  };

  const reset = () => {
    setTitle(item.title);
    setOrigin(item.origin ?? "");
    setDueDate(item.dueDate ?? "");
    setDueTime(item.dueTime ?? "");
  };

  const removeIt = () =>
    remove.mutate(item, {
      onSuccess: () =>
        ui.showToast(item.kind === "schedule" ? "Removida de A agendar" : "Demanda excluída", {
          label: "Desfazer",
          run: () =>
            create.mutate(
              { title: item.title, origin: item.origin, kind: item.kind, dueDate: item.dueDate, dueTime: item.dueTime },
              { onSuccess: (n) => item.completed && update.mutate({ item: n, completed: true }) }
            )
        }),
      onError: fail
    });

  return (
    <li className={`row${item.completed ? " done" : ""}`}>
      <label className="checkbox round row-check">
        <input type="checkbox" checked={item.completed} disabled={saving} onChange={toggle} aria-label={item.completed ? `Reabrir ${item.title}` : `Resolver ${item.title}`} />
      </label>
      {editing ? (
        <form
          className="row-main row-edit-pair"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
          onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && save()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              reset();
              setEditing(false);
            }
          }}
        >
          <input className="field row-edit" autoFocus value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} aria-label="O que precisa ser feito" />
          {item.kind === "demand" && (
            <input className="field row-edit sm" value={origin} maxLength={120} onChange={(e) => setOrigin(e.target.value)} placeholder="Quem pediu" aria-label="Quem pediu" />
          )}
          <input className="field row-edit date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} aria-label="Data (entra na agenda, com lembrete)" />
          {dueDate && <input className="field row-edit time" type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} aria-label="Horário (opcional)" />}
          <button type="submit" hidden />
        </form>
      ) : (
        <button className="row-main" disabled={saving} onClick={() => setEditing(true)} title="Clique para editar">
          <span className="row-stack">
            <span className="row-title">{item.title}</span>
            {(item.origin || item.dueDate) && (
              <span className="row-sub">
                <DueLine item={item} today={ui.today} />
                {item.origin && item.dueDate && " · "}
                {item.origin && `Pedido por ${item.origin}`}
              </span>
            )}
          </span>
        </button>
      )}
      <span className="row-meta">
        <button className="icon-btn sm row-delete" aria-label={`Excluir ${item.title}`} disabled={saving} onClick={removeIt}>
          <Trash2 size={15} />
        </button>
      </span>
    </li>
  );
}

function DemandAdd({ kind }: { kind: HealthDemand["kind"] }) {
  const ui = useUi();
  const { create } = useHealthDemandMutations();
  const [title, setTitle] = useState("");
  const [origin, setOrigin] = useState("");
  const [dueDate, setDueDate] = useState("");
  return (
    <form
      className="add-line"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        create.mutate(
          { title: t, origin: origin.trim() || null, kind, dueDate: dueDate || null },
          {
            onSuccess: (row) => row.onAgenda && ui.showToast("Também está na agenda, com lembrete"),
            onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`)
          }
        );
        setTitle("");
        setOrigin("");
        setDueDate("");
      }}
    >
      <input
        className="add-input"
        value={title}
        maxLength={300}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={kind === "schedule" ? "Consulta a agendar (ex: nutricionista)…" : "O que a consulta pediu…"}
        aria-label={kind === "schedule" ? "Nova consulta a agendar" : "Nova demanda"}
      />
      {kind === "demand" && (
        <>
          <span className="add-divider" aria-hidden />
          <input className="add-input add-input-sm" value={origin} maxLength={120} onChange={(e) => setOrigin(e.target.value)} placeholder="Quem pediu" aria-label="Quem pediu (pessoa ou médico)" />
        </>
      )}
      <span className="add-divider" aria-hidden />
      <input
        className={`add-input add-input-date${dueDate ? "" : " empty"}`}
        type="date"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        aria-label="Data, se tiver (entra na agenda, com lembrete)"
        title="Data, se tiver — entra na agenda, com lembrete"
      />
      <button type="submit" className="btn primary add-submit" disabled={!title.trim()} aria-label="Adicionar demanda">
        <ArrowRight size={16} />
      </button>
    </form>
  );
}

function Demands() {
  const { data, isLoading } = useHealthDemands();
  const pending = (data?.pending ?? []).filter((d) => d.kind === "demand");
  const done = (data?.completed ?? []).filter((d) => d.kind === "demand");
  return (
    <>
      <DemandAdd kind="demand" />
      <p className="life-hint below-add">
        Comprar um remédio, marcar um exame, voltar com o resultado — e quem pediu. Com data, também entra na agenda, com lembrete no WhatsApp. Pelo WhatsApp: “a dra. Ana pediu
        hemograma até sexta”, “já fiz o hemograma”.
      </p>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {!pending.length ? (
            <p className="empty">Nada pendente das consultas.</p>
          ) : (
            <RowSection title="Pendentes" count={pending.length}>
              {pending.map((d) => <DemandRow key={d.id} item={d} />)}
            </RowSection>
          )}
          <DoneSection title="Resolvidas · 30 dias" count={done.length}>
            {done.map((d) => <DemandRow key={d.id} item={d} />)}
          </DoneSection>
        </>
      )}
    </>
  );
}

// --- Cuidados periódicos -------------------------------------------------------------------------

export const INTERVALS: Array<{ months: number; label: string }> = [
  { months: 1, label: "Todo mês" },
  { months: 2, label: "A cada 2 meses" },
  { months: 3, label: "A cada 3 meses" },
  { months: 4, label: "A cada 4 meses" },
  { months: 6, label: "A cada 6 meses" },
  { months: 12, label: "Todo ano" },
  { months: 24, label: "A cada 2 anos" },
  { months: 36, label: "A cada 3 anos" },
  { months: 60, label: "A cada 5 anos" },
  { months: 120, label: "A cada 10 anos" }
];

function intervalLabel(months: number): string {
  return INTERVALS.find((i) => i.months === months)?.label ?? (months % 12 === 0 ? `A cada ${months / 12} anos` : `A cada ${months} meses`);
}

/** Days before the next time at which a care turns yellow. */
const SOON_DAYS = 30;

function routineStatus(r: HealthRoutine, today: DateStr) {
  if (!r.lastDone) return { next: null, tone: "muted" as const, label: "Sem data" };
  const next = addMonths(r.lastDone, r.intervalMonths);
  const days = diffDays(today, next);
  if (days < 0) return { next, tone: "danger" as const, label: `Atrasado ${-days} ${-days === 1 ? "dia" : "dias"}` };
  if (days === 0) return { next, tone: "warning" as const, label: "É hoje" };
  if (days <= SOON_DAYS) return { next, tone: "warning" as const, label: `Em ${days} ${days === 1 ? "dia" : "dias"}` };
  return { next, tone: "success" as const, label: "Em dia" };
}

function RoutineForm({ initial, onSubmit, onCancel, submitLabel }: { initial: RoutineInput; onSubmit: (v: RoutineInput) => void; onCancel?: () => void; submitLabel: string }) {
  const [v, setV] = useState(initial);
  const valid = v.title.trim().length > 0;
  return (
    <form
      className="life-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({ ...v, title: v.title.trim(), person: v.person?.trim() || null, note: v.note?.trim() || null });
        if (!onCancel) setV(initial);
      }}
    >
      <label className="life-field grow">
        <span>O que é</span>
        <input className="field filled" value={v.title} maxLength={120} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Dentista, exames de rotina, vacina…" />
      </label>
      <label className="life-field">
        <span>Para quem</span>
        <input className="field filled" value={v.person ?? ""} maxLength={80} onChange={(e) => setV({ ...v, person: e.target.value })} placeholder="Opcional" />
      </label>
      <label className="life-field">
        <span>Última vez</span>
        <input className="field filled" type="date" value={v.lastDone ?? ""} onChange={(e) => setV({ ...v, lastDone: e.target.value || null })} />
      </label>
      <label className="life-field">
        <span>Frequência</span>
        <select className="chip-select life-select" value={v.intervalMonths} onChange={(e) => setV({ ...v, intervalMonths: Number(e.target.value) })}>
          {/* A care saved by message can have any interval ("a cada 18 meses"): it stays selectable. */}
          {(INTERVALS.some((i) => i.months === v.intervalMonths) ? INTERVALS : [...INTERVALS, { months: v.intervalMonths, label: intervalLabel(v.intervalMonths) }].sort((a, b) => a.months - b.months)).map((i) => (
            <option key={i.months} value={i.months}>
              {i.label}
            </option>
          ))}
        </select>
      </label>
      <label className="life-field grow">
        <span>Observação</span>
        <input className="field filled" value={v.note ?? ""} maxLength={200} onChange={(e) => setV({ ...v, note: e.target.value })} placeholder="Clínica, médico, detalhes…" />
      </label>
      <div className="life-form-actions">
        {onCancel && (
          <button type="button" className="btn text" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn primary" disabled={!valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function RoutineRow({ r }: { r: HealthRoutine }) {
  const ui = useUi();
  const { create, update, remove } = useHealthRoutineMutations();
  const [editing, setEditing] = useState(false);
  const saving = r.id.startsWith("tmp-");
  const st = routineStatus(r, ui.today);
  const fail = (err: unknown) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`);

  if (editing) {
    return (
      <li className="row row-editing">
        <RoutineForm
          initial={r}
          submitLabel="Salvar"
          onCancel={() => setEditing(false)}
          onSubmit={(v) => {
            setEditing(false);
            update.mutate({ item: r, ...v }, { onError: fail });
          }}
        />
      </li>
    );
  }

  const doneToday = () =>
    update.mutate(
      { item: r, lastDone: ui.today },
      {
        onSuccess: () => ui.showToast(`${r.title}: feito hoje`, { label: "Desfazer", run: () => update.mutate({ item: { ...r, lastDone: ui.today }, lastDone: r.lastDone }) }),
        onError: fail
      }
    );

  return (
    <li className="row care-row">
      <button className="row-main" disabled={saving} onClick={() => setEditing(true)} title="Clique para editar">
        <span className="row-stack">
          <span className="row-title">
            {r.title}
            {r.person && <span className="row-title-aside"> · {r.person}</span>}
          </span>
          <span className="row-sub">
            {intervalLabel(r.intervalMonths)}
            {r.lastDone ? ` · última vez ${brDate(r.lastDone)}` : ""}
            {st.next ? ` · próxima ${brDate(st.next)}` : ""}
            {r.onAgenda ? " · na agenda" : ""}
            {r.note ? ` · ${r.note}` : ""}
          </span>
        </span>
      </button>
      <span className="row-meta">
        <StatusChip tone={st.tone}>{st.label}</StatusChip>
        {r.lastDone !== ui.today && (
          <button className="btn text care-done" disabled={saving} onClick={doneToday}>
            Feito hoje
          </button>
        )}
        <button
          className="icon-btn sm row-delete"
          aria-label={`Excluir ${r.title}`}
          disabled={saving}
          onClick={() =>
            remove.mutate(r, {
              onSuccess: () => ui.showToast("Cuidado excluído", { label: "Desfazer", run: () => create.mutate({ title: r.title, person: r.person, lastDone: r.lastDone, intervalMonths: r.intervalMonths, note: r.note }) }),
              onError: fail
            })
          }
        >
          <Trash2 size={15} />
        </button>
      </span>
    </li>
  );
}

const EMPTY_ROUTINE: RoutineInput = { title: "", person: null, lastDone: null, intervalMonths: 12, note: null };

function Routines() {
  const ui = useUi();
  const { data, isLoading } = useHealthRoutines();
  const { create } = useHealthRoutineMutations();
  // Most urgent first: overdue, then by next date; undated last.
  const sorted = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => {
        const na = a.lastDone ? addMonths(a.lastDone, a.intervalMonths) : "9999";
        const nb = b.lastDone ? addMonths(b.lastDone, b.intervalMonths) : "9999";
        return na.localeCompare(nb);
      }),
    [data]
  );
  return (
    <>
      <p className="life-hint">
        Informe a última vez e a frequência — a próxima data é calculada (verde em dia, amarelo chegando em 30 dias, vermelho atrasado) e entra na agenda como tarefa, com lembrete no WhatsApp
        uma semana antes e no dia. Pelo WhatsApp: “dentista a cada 6 meses, fui em abril”, “fui ao dentista hoje”.
      </p>
      {isLoading ? (
        <Spinner />
      ) : !sorted.length ? (
        <p className="empty">Nenhum cuidado cadastrado.</p>
      ) : (
        <RowSection title="Cuidados" count={sorted.length}>
          {sorted.map((r) => <RoutineRow key={r.id} r={r} />)}
        </RowSection>
      )}
      <CollapsibleSection title="Adicionar cuidado" storageKey="health.addRoutineOpen">
        <RoutineForm
          initial={EMPTY_ROUTINE}
          submitLabel="Adicionar"
          onSubmit={(v) => create.mutate(v, { onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`) })}
        />
      </CollapsibleSection>
    </>
  );
}

function Spinner() {
  return (
    <div className="center-fill" style={{ height: 200 }}>
      <div className="spinner" />
    </div>
  );
}

/** "Saúde": appointments (from the agenda), what they asked for, and periodic care. */
export function HealthPage() {
  const ui = useUi();
  const [tab, setTab] = useStoredTab<Tab>("health.tab", ["consultas", "demandas", "cuidados"]);
  const pendingDemands = useHealthDemands().data?.pending ?? [];
  const demands = pendingDemands.filter((d) => d.kind === "demand").length;
  const toSchedule = pendingDemands.length - demands;
  const routines = useHealthRoutines().data ?? [];
  const late = routines.filter((r) => routineStatus(r, ui.today).tone === "danger").length;

  return (
    <div className="page-scroll">
      <div className="page">
        <PageHead label="Saúde" title="Consultas e cuidados.">
          O que está marcado, o que as consultas pediram e o que não pode passar do prazo.
        </PageHead>
        <Segmented
          value={tab}
          onChange={setTab}
          label="Áreas da saúde"
          options={[
            { value: "consultas", label: "Consultas", count: toSchedule },
            { value: "demandas", label: "Demandas", count: demands },
            { value: "cuidados", label: "Cuidados periódicos", count: late, tone: "danger" }
          ]}
        />
        {tab === "consultas" ? <Appointments /> : tab === "demandas" ? <Demands /> : <Routines />}
      </div>
    </div>
  );
}
